import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { breakout } from '@atlaskit/adf-schema';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { BreakoutCssClassName } from '@atlaskit/editor-common/styles';
import { calcBreakoutWidthPx } from '@atlaskit/editor-common/utils';
import { akEditorGutterPaddingDynamic, akEditorSwoopCubicBezier } from '@atlaskit/editor-shared-styles';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { pluginKey } from './plugin-key';
import LayoutButton from './ui/LayoutButton';
import { findSupportedNodeForBreakout } from './utils/find-breakout-node';
var BreakoutView = /*#__PURE__*/function () {
  function BreakoutView(
  /**
   * Note: this is actually a PMMark -- however our version
   * of the prosemirror and prosemirror types mean using PMNode
   * is not problematic.
   */
  mark, view, pluginInjectionApi) {
    var _this = this,
      _pluginInjectionApi$w,
      _pluginInjectionApi$w2;
    _classCallCheck(this, BreakoutView);
    _defineProperty(this, "updateWidth", function (widthState) {
      // we skip updating the width of breakout nodes if the editorView dom
      // element was hidden (to avoid breakout width and button thrashing
      // when an editor is hidden, re-rendered and unhidden).
      if (widthState === undefined || widthState.width === 0) {
        return;
      }
      var containerStyle = "";
      var contentStyle = "";

      // when editor padding = 32px the breakout padding is calculated as 96px (32 * 3)
      // the extra '32' ensures nodes with breakout applied default to line length its below default width
      var padding = getBooleanFF('platform.editor.core.increase-full-page-guttering') ? akEditorGutterPaddingDynamic() * 2 + 32 : undefined;
      var breakoutWidthPx = calcBreakoutWidthPx(_this.mark.attrs.mode, widthState.width, padding);
      if (widthState.lineLength) {
        if (breakoutWidthPx < widthState.lineLength) {
          breakoutWidthPx = widthState.lineLength;
        }
        containerStyle += "\n        transform: none;\n        display: flex;\n        justify-content: center;\n        ";

        // There is a delay in the animation because widthState is delayed.
        // When the editor goes full width the animation for the editor
        // begins and finishes before widthState can update the new dimensions.
        contentStyle += "\n        min-width: ".concat(breakoutWidthPx, "px;\n        transition: min-width 0.5s ").concat(akEditorSwoopCubicBezier, ";\n      ");
      } else {
        // fallback method
        // (lineLength is not normally undefined, but might be in e.g. SSR or initial render)
        //
        // this approach doesn't work well with position: fixed, so
        // it breaks things like sticky headers
        containerStyle += "width: ".concat(breakoutWidthPx, "px; transform: translateX(-50%); margin-left: 50%;");
      }

      // NOTE: This is a hack to ignore mutation since mark NodeView doesn't support
      // `ignoreMutation` life-cycle event. @see ED-9947
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      var viewDomObserver = _this.view.domObserver;
      if (viewDomObserver && _this.view.dom) {
        viewDomObserver.stop();
        setTimeout(function () {
          viewDomObserver.start();
        }, 0);
      }
      if (typeof _this.dom.style.cssText !== 'undefined') {
        _this.dom.style.cssText = containerStyle;
        _this.contentDOM.style.cssText = contentStyle;
      } else {
        _this.dom.setAttribute('style', containerStyle);
        _this.contentDOM.setAttribute('style', contentStyle);
      }
    });
    var contentDOM = document.createElement('div');
    contentDOM.className = BreakoutCssClassName.BREAKOUT_MARK_DOM;
    var dom = document.createElement('div');
    dom.className = BreakoutCssClassName.BREAKOUT_MARK;
    dom.setAttribute('data-layout', mark.attrs.mode);
    dom.appendChild(contentDOM);
    this.dom = dom;
    this.mark = mark;
    this.view = view;
    this.contentDOM = contentDOM;
    this.unsubscribe = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$w = pluginInjectionApi.width) === null || _pluginInjectionApi$w === void 0 ? void 0 : _pluginInjectionApi$w.sharedState.onChange(function (_ref) {
      var nextSharedState = _ref.nextSharedState;
      return _this.updateWidth(nextSharedState);
    });
    this.updateWidth(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$w2 = pluginInjectionApi.width) === null || _pluginInjectionApi$w2 === void 0 ? void 0 : _pluginInjectionApi$w2.sharedState.currentState());
  }
  _createClass(BreakoutView, [{
    key: "destroy",
    value:
    // NOTE: Lifecycle events doesn't work for mark NodeView. So currently this is a no-op.
    // @see https://github.com/ProseMirror/prosemirror/issues/1082
    function destroy() {
      var _this$unsubscribe;
      (_this$unsubscribe = this.unsubscribe) === null || _this$unsubscribe === void 0 || _this$unsubscribe.call(this);
    }
  }]);
  return BreakoutView;
}();
function shouldPluginStateUpdate(newBreakoutNode, currentBreakoutNode) {
  if (newBreakoutNode && currentBreakoutNode) {
    return newBreakoutNode !== currentBreakoutNode;
  }
  return newBreakoutNode || currentBreakoutNode ? true : false;
}
function createPlugin(pluginInjectionApi, _ref2) {
  var dispatch = _ref2.dispatch;
  return new SafePlugin({
    state: {
      init: function init() {
        return {
          breakoutNode: undefined
        };
      },
      apply: function apply(tr, pluginState) {
        var breakoutNode = findSupportedNodeForBreakout(tr.selection);
        if (shouldPluginStateUpdate(breakoutNode, pluginState.breakoutNode)) {
          var nextPluginState = _objectSpread(_objectSpread({}, pluginState), {}, {
            breakoutNode: breakoutNode
          });
          dispatch(pluginKey, nextPluginState);
          return nextPluginState;
        }
        return pluginState;
      }
    },
    key: pluginKey,
    props: {
      nodeViews: {
        // Note: When we upgrade to prosemirror 1.27.2 -- we should
        // move this to markViews.
        // See the following link for more details:
        // https://prosemirror.net/docs/ref/#view.EditorProps.nodeViews.
        breakout: function breakout(mark, view) {
          return new BreakoutView(mark, view, pluginInjectionApi);
        }
      }
    }
  });
}
var LayoutButtonWrapper = function LayoutButtonWrapper(_ref3) {
  var _breakoutState$breako, _breakoutState$breako2;
  var api = _ref3.api,
    editorView = _ref3.editorView,
    boundariesElement = _ref3.boundariesElement,
    scrollableElement = _ref3.scrollableElement,
    mountPoint = _ref3.mountPoint;
  // Re-render with `width` (but don't use state) due to https://bitbucket.org/atlassian/%7Bc8e2f021-38d2-46d0-9b7a-b3f7b428f724%7D/pull-requests/24272
  var _useSharedPluginState = useSharedPluginState(api, ['width', 'breakout', 'editorViewMode']),
    breakoutState = _useSharedPluginState.breakoutState,
    editorViewModeState = _useSharedPluginState.editorViewModeState;
  var isViewMode = (editorViewModeState === null || editorViewModeState === void 0 ? void 0 : editorViewModeState.mode) === 'view';
  var isEditMode = (editorViewModeState === null || editorViewModeState === void 0 ? void 0 : editorViewModeState.mode) === 'edit';
  return !isViewMode ? /*#__PURE__*/React.createElement(LayoutButton, {
    editorView: editorView,
    mountPoint: mountPoint,
    boundariesElement: boundariesElement,
    scrollableElement: scrollableElement,
    node: (_breakoutState$breako = breakoutState === null || breakoutState === void 0 || (_breakoutState$breako2 = breakoutState.breakoutNode) === null || _breakoutState$breako2 === void 0 ? void 0 : _breakoutState$breako2.node) !== null && _breakoutState$breako !== void 0 ? _breakoutState$breako : null,
    isLivePage: isEditMode
  }) : null;
};
export var breakoutPlugin = function breakoutPlugin(_ref4) {
  var options = _ref4.config,
    api = _ref4.api;
  return {
    name: 'breakout',
    pmPlugins: function pmPlugins() {
      return [{
        name: 'breakout',
        plugin: function plugin(props) {
          return createPlugin(api, props);
        }
      }];
    },
    marks: function marks() {
      return [{
        name: 'breakout',
        mark: breakout
      }];
    },
    getSharedState: function getSharedState(editorState) {
      if (!editorState) {
        return {
          breakoutNode: undefined
        };
      }
      var pluginState = pluginKey.getState(editorState);
      if (!pluginState) {
        return {
          breakoutNode: undefined
        };
      }
      return pluginState;
    },
    contentComponent: function contentComponent(_ref5) {
      var editorView = _ref5.editorView,
        popupsMountPoint = _ref5.popupsMountPoint,
        popupsBoundariesElement = _ref5.popupsBoundariesElement,
        popupsScrollableElement = _ref5.popupsScrollableElement;
      // This is a bit crappy, but should be resolved once we move to a static schema.
      if (options && !options.allowBreakoutButton || !editorView.editable) {
        return null;
      }
      return /*#__PURE__*/React.createElement(LayoutButtonWrapper, {
        api: api,
        mountPoint: popupsMountPoint,
        editorView: editorView,
        boundariesElement: popupsBoundariesElement,
        scrollableElement: popupsScrollableElement
      });
    }
  };
};